// The stored session (03-startup-session.md, "Stored in this browser"; 90-test-hooks.md): one JSON
// record under `desyr_session`. All four required fields present = signed in.
import { readJson, writeJson } from './storage';

export interface Session {
  /** The chat server's base URL, no trailing slash. */
  base_url: string;
  user_id: string;
  device_id: string;
  access_token: string;
  refresh_token?: string;
  /** A session from desyr ID's sessions call (36, Behaviour 6): when it ends (ms), the service's handle, the circle's slug. */
  expires_at?: number;
  session_id?: string;
  circle?: string;
}

const KEY = 'desyr_session';
const REQUIRED = ['base_url', 'user_id', 'device_id', 'access_token'] as const;

export function readSession(): Session | undefined {
  const s = readJson<Record<string, unknown>>(KEY);
  if (!s || REQUIRED.some((f) => typeof s[f] !== 'string' || !s[f])) return undefined;
  return s as unknown as Session;
}

export const writeSession = (s: Session) => writeJson(KEY, { ...s, base_url: s.base_url.replace(/\/+$/, '') });

/** The server part of a user ID: "@chloe:autarq.localhost" → "autarq.localhost". */
export const serverOf = (userId: string) => userId.slice(userId.indexOf(':') + 1);

/** An account as its own chat server sees it: enough for a call with its own token. */
export type Who = Pick<Session, 'user_id' | 'base_url' | 'access_token'>;
/**
 * desyr reloads itself (a switch, 33): requests still under way end here, caught, and any asked for
 * after it end at once, instead of being cut off by the reload (which Safari reports as errors).
 */
const leaving = new AbortController();
const leaveHooks = new Set<() => void>();
export const onLeave = (hook: () => void) => (leaveHooks.add(hook), () => void leaveHooks.delete(hook));
export function leave(): void {
  leaving.abort();
  leaveHooks.forEach((hook) => hook());
}
/** `fetch` that, once the page is leaving, ends every new request at once instead of sending it. */
export const fetchUntilLeaving = (send: typeof fetch = fetch): typeof fetch => (input, init) =>
  leaving.signal.aborted ? Promise.reject(new DOMException('desyr is reloading', 'AbortError')) : send(input, init);

let quitting = false;
/** Signing out is under way (03): an ended token is neither renewed nor reported as "signed out elsewhere". */
export const signingOut = () => void (quitting = true);
export const isSigningOut = () => quitting;

/** A call to an account's chat server (`/_matrix/client` + `path`) with its own token. */
export const ask = (a: Who, path: string, init: RequestInit = {}) =>
  fetch(`${a.base_url}/_matrix/client${path}`, { signal: leaving.signal, ...init, headers: { Authorization: `Bearer ${a.access_token}`, ...(init.body ? { 'Content-Type': 'application/json' } : {}) } });
